/**
 * Session guards for Credit Report auto-refresh (one attempt per report/provider;
 * no re-auto-start after user dismisses import progress).
 */

const ATTEMPTED_PREFIX = 'credit-report:auto-refresh-attempted';
const DISMISSED_PREFIX = 'credit-report:auto-refresh-dismissed';

function normalizeProviderKey(provider) {
  return provider?.toLowerCase().replace(/\s/g, '').replace(/-/g, '').replace(/_/g, '') || '';
}

export function getAutoRefreshGuardKey(provider, reportId) {
  const providerKey = normalizeProviderKey(provider);
  const id = reportId || 'unknown';
  return `${providerKey}:${id}`;
}

function readFlag(prefix, provider, reportId) {
  try {
    const key = `${prefix}:${getAutoRefreshGuardKey(provider, reportId)}`;
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(prefix, provider, reportId, value) {
  try {
    const key = `${prefix}:${getAutoRefreshGuardKey(provider, reportId)}`;
    if (value) {
      sessionStorage.setItem(key, '1');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

export function isAutoRefreshAttempted(provider, reportId) {
  return readFlag(ATTEMPTED_PREFIX, provider, reportId);
}

export function isAutoRefreshDismissed(provider, reportId) {
  return readFlag(DISMISSED_PREFIX, provider, reportId);
}

export function canAutoStartRefresh(provider, reportId, { activeImportId = null } = {}) {
  if (activeImportId) return false;
  if (isAutoRefreshDismissed(provider, reportId)) return false;
  if (isAutoRefreshAttempted(provider, reportId)) return false;
  return true;
}

export function markAutoRefreshAttempted(provider, reportId) {
  writeFlag(ATTEMPTED_PREFIX, provider, reportId, true);
}

export function markAutoRefreshDismissed(provider, reportId) {
  writeFlag(DISMISSED_PREFIX, provider, reportId, true);
}

/** After a successful import, block auto-start for this provider/report (and optional new report id). */
export function markRefreshHandledAfterCompletion(provider, reportId, newReportId = null) {
  if (!provider) return;
  if (reportId) {
    markAutoRefreshAttempted(provider, reportId);
    markAutoRefreshDismissed(provider, reportId);
  }
  if (newReportId && newReportId !== reportId) {
    markAutoRefreshAttempted(provider, newReportId);
    markAutoRefreshDismissed(provider, newReportId);
  }
}

export function clearAutoRefreshDismissed(provider, reportId) {
  writeFlag(DISMISSED_PREFIX, provider, reportId, false);
}

export function clearAutoRefreshGuards(provider, reportId) {
  writeFlag(ATTEMPTED_PREFIX, provider, reportId, false);
  writeFlag(DISMISSED_PREFIX, provider, reportId, false);
}
